/**
 * Atividade da sessão.
 *
 * O sistema lida com dinheiro e conta digital, mas quem opera precisa passar o
 * dia dentro dele — encurtar a sessão por relógio atrapalharia o atendimento
 * sem proteger muita coisa. O que separa "operador trabalhando" de "estação
 * abandonada no balcão" é interação, não tempo decorrido: enquanto houver uso,
 * a sessão se renova sozinha; parou, ela morre.
 *
 * A marcação vive no localStorage de propósito: é compartilhada entre as abas,
 * então trabalhar em uma aba mantém as outras vivas.
 */
const ACTIVITY_KEY = 'last_activity'

/** Sem interação por este tempo, a sessão é encerrada. */
export const IDLE_LIMIT_MS = 30 * 60 * 1000

/**
 * Vida máxima da sessão local — igual ao refresh token do backend
 * (REFRESH_TOKEN_TTL). Passado isso, nem a renovação silenciosa salva.
 */
export const SESSION_LIFETIME_MS = 12 * 60 * 60 * 1000

/** Evita escrever no localStorage a cada evento de mouse. */
const WRITE_THROTTLE_MS = 30 * 1000

let lastWrite = 0

export const markActivity = (force = false) => {
    if (typeof window === 'undefined') return

    const now = Date.now()
    if (!force && now - lastWrite < WRITE_THROTTLE_MS) return

    lastWrite = now
    try {
        localStorage.setItem(ACTIVITY_KEY, String(now))
    } catch {
        // localStorage indisponível (janela anônima com restrição): não é
        // motivo para derrubar a sessão.
    }
}

export const getLastActivity = (): number | null => {
    if (typeof window === 'undefined') return null

    const raw = localStorage.getItem(ACTIVITY_KEY)
    const parsed = raw ? parseInt(raw, 10) : NaN

    return Number.isFinite(parsed) ? parsed : null
}

/**
 * Sessão parada além do limite. Sem marcação nenhuma (sessão recém-criada, ou
 * navegador que bloqueia storage) não conta como ociosa — quem decide aí é a
 * expiração do próprio token.
 */
export const isSessionIdle = (): boolean => {
    const last = getLastActivity()
    if (last === null) return false

    return Date.now() - last > IDLE_LIMIT_MS
}

export const clearActivity = () => {
    lastWrite = 0
    try {
        localStorage.removeItem(ACTIVITY_KEY)
    } catch {
        // idem
    }
}
